import { Routes } from '@angular/router';
import { LoginComponent } from './login/login.component';
import { AuthGuard } from './auth/auth.guard';
import { ResetPasswordComponent } from './login/reset-password/reset-password.component';
import { ReportsComponent } from './reports/reports.component';
import { CasesComponent } from './cases/cases.component';
import { UserListComponent } from './admin/user-list/user-list.component';
import { InformationComponent } from './information/information.component';
import { FaqComponent } from './information/faq.component';
import { AddEditUserComponent } from './admin/add-edit-user/add-edit-user.component';
import { AddSubCustomerComponent } from './admin/add-sub-customer/add-sub-customer.component';
import { UserGuard } from './admin/user-guard';
import { IRouteData, LogicalOperator, UserRole, WebUserRight } from './shared/models';
import { ActiveCasesComponent } from './reports/active-cases/active-cases.component';
import { ClosedCasesComponent } from './reports/closed-cases/closed-cases.component';
import { CustomReportsComponent } from './reports/custom-reports/custom-reports.component';
import { EconomicComponent } from './reports/economic/economic.component';
import { OutstandingAmountComponent } from './reports/outstanding-amount/outstanding-amount.component';
import { PerformanceComponent } from './reports/performance/performance.component';
import { PortfolioAnalysisComponent } from './reports/portfolio-analysis/portfolio-analysis.component';
import { ReportGuard } from './reports/report-guard';
import { ScoreComponent } from './reports/score/score.component';
import { StatusComponent } from './reports/status/status.component';
import { SuccessRateComponent } from './reports/success-rate/success-rate.component';
import { CasesRootComponent } from './cases/cases-root/cases-root.component';
import { LastAccessedCasesComponent } from './cases/last-accessed-cases/last-accessed-cases.component';
import { NewCaseComponent } from './cases/new-case/new-case.component';
import { ToDebtCollectionComponent } from './cases/to-debt-collection/to-debt-collection.component';
import { ContractsComponent } from './contracts/contracts/contracts.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { CasePageComponent } from './cases/case/case-page.component';
import { UserProfileComponent } from './user/profile/user-profile.component';
import { TreatedCasesComponent } from './reports/treated-cases/treated-cases.component';
import { ContractScoreComponent } from './reports/contract-score/contract-score.component';

const protectedBy = (right: WebUserRight = null, role: UserRole = null, comparison: LogicalOperator = 'Or') => ({ userRight: right, userRole: role, comparison } as IRouteData)

export const routes: Routes = [
  { path: 'login', component: LoginComponent, canActivate: [AuthGuard] },
  { path: 'login/:requestId', component: LoginComponent, canActivate: [AuthGuard] },
  { path: 'reset-password/:requestId', component: ResetPasswordComponent },
  { path: 'unauthorized', component: LoginComponent },
  { path: 'dashboard', component: DashboardComponent, canActivate: [AuthGuard] },
  {
    path: 'information', component: InformationComponent, canActivate: [AuthGuard], canActivateChild: [AuthGuard],
    children: [
      { path: 'faq', canActivate: [AuthGuard], component: FaqComponent }
    ]
  },
  {
    path: 'search', loadComponent: () =>
      import('./search/search.component').then(m => m.SearchComponent), canActivate: [AuthGuard]
  },
  {
    path: 'search/Iper', loadComponent: () =>
      import('./search/iper-search/iper-search.component').then(m => m.IperSearchComponent), canActivate: [AuthGuard],
    pathMatch: 'full'
  },
  {
    path: 'search/:provider', loadComponent: () =>
      import('./search/third-party-search.component').then(m => m.ThirdPartySearchComponent), canActivate: [AuthGuard]
  },
  {
    path: 'admin', canActivate: [AuthGuard], canActivateChild: [AuthGuard],
    children: [
      { path: 'userList', component: UserListComponent, canActivate: [AuthGuard], data: protectedBy(null, 'SuperUser') },
      { path: 'addSubCustomer', component: AddSubCustomerComponent, canActivate: [AuthGuard], data: protectedBy(WebUserRight.AddSubCustomer, 'SuperUser', 'And') },
      { path: 'user', component: AddEditUserComponent, canActivate: [AuthGuard], data: protectedBy(WebUserRight.AddUser, 'SuperUser', 'And') },
      { path: 'user/:userCode', component: AddEditUserComponent, canActivate: [AuthGuard, UserGuard], data: protectedBy(null, 'SuperUser') },
    ]
  },
  { 
    path: 'user', canActivate: [AuthGuard], canActivateChild: [AuthGuard],
    children: [
      { path: 'profile', component: UserProfileComponent },
    ]
  }, 
  {
    path: 'reports', component: ReportsComponent, canActivate: [AuthGuard], canActivateChild: [AuthGuard], children: [
      { path: 'customReports', component: CustomReportsComponent }
      , { path: 'performance', component: PerformanceComponent }
      , { path: 'portfolioAnalysis', component: PortfolioAnalysisComponent }
      , { path: 'activeCases', component: ActiveCasesComponent, canActivate: [ReportGuard], data: { ReportName: 'ActiveCases' } }
      , { path: 'closedCases', component: ClosedCasesComponent, canActivate: [ReportGuard], data: { ReportName: 'ClosedCases' } }
      , { path: 'treatedCases', component: TreatedCasesComponent, canActivate: [ReportGuard], data: { ReportName: 'TreatedCases' } }
      , { path: 'outstandingAmount', component: OutstandingAmountComponent }
      , { path: 'status', component: StatusComponent }
      , { path: 'contractScore', component: ContractScoreComponent, data: protectedBy(WebUserRight.AccessContractAdmin) }
      , { path: 'successRate', component: SuccessRateComponent }
      , { path: 'score', component: ScoreComponent }
      , { path: 'economic', component: EconomicComponent }
    ]
  },
  {
    path: 'documents', loadComponent: () => import('./documents/documents.component').then(m => m.DocumentsComponent),
    canActivate: [AuthGuard]
  },
  {
    path: 'cases', component: CasesRootComponent, canActivate: [AuthGuard], canActivateChild: [AuthGuard], children: [
      { path: 'contracts', component: ContractsComponent, data: protectedBy(WebUserRight.AccessContractAdmin) },
      { path: 'new', component: NewCaseComponent, canActivate: [AuthGuard], data: protectedBy(WebUserRight.CreateCase) },
      { path: 'lists', component: CasesComponent, canActivate: [AuthGuard] },
      { path: 'lastAccessed', component: LastAccessedCasesComponent, canActivate: [AuthGuard] },
      { path: 'todebtcollection', component: ToDebtCollectionComponent, canActivate: [AuthGuard], data: protectedBy(WebUserRight.ToDebtCollectionList) },
      { path: ':caseNumber', component: CasePageComponent, canActivate: [AuthGuard] }
    ]
  },
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' }
];
